/* eslint-disable import/first */

// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  SearchkitProvider,
  ResetSearchAccessor,
  BoolMust,
  BoolShould,
  TermQuery,
  TermsQuery,
  MenuFilter,
  ResetFilters,
  SortingSelector,
} from 'searchkit';
import qs from 'qs';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import omit from 'lodash/omit';

// app modules: actions
import { searchIsEmpty, searchIsLoading } from 'actions/search';
import { featureUsed } from 'actions/featureUsage';
import { logAccess, ACCESS_TYPE_NORMAL } from 'actions/logging';

// app modules: components
import withFeatureToggle from 'components/FeatureToggleHoC';

// app modules: services
import { sendEvent } from 'services/analytics';
import history from 'services/history';
import search from 'services/search';
import SearchkitManagerCustom from 'services/SearchkitManagerCustom';
import { dietWasTouched, ownedFilterFields } from 'services/searchFilterScope';

// app modules: constants
import {
  FILTERS,
  SORT_OPTIONS,
  CULINARY_PREFER_BOOST,
  CULINARY_PREFER_CLAUSE_NAME,
  CULINARY_REGION_PREFER_CLAUSE_NAME,
  NUTRITION_DIET_PREFER_BOOST,
  OWNED_FILTERS_QUERY_KEY,
  SAMPLE_SEARCH_PARAM,
  SHARED_FILTERS,
} from 'constants/search';
import { CONTENT_TYPES } from 'constants/index';
import {
  CULINARY_PREFER_FIELDS,
  getCulinarySearchValues,
  getCulinaryRegionTerms,
} from 'constants/culinary-interest-options';
import {
  getNutritionDietSearchValues,
  getPrimaryNutritionDietSearchValues,
} from 'constants/nutrition-diet-options';

export const SearchkitContext = React.createContext();
export class SearchProvider extends Component {
  static propTypes = {
    alacarteEnabled: PropTypes.bool.isRequired,
    allowEmptySearch: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
    connected: PropTypes.bool,
    defaultQuery: PropTypes.func,
    featureUsed: PropTypes.func.isRequired,
    getLocation: PropTypes.func,
    includeTags: PropTypes.arrayOf(PropTypes.string),
    isBackNavigation: PropTypes.bool,
    logAccess: PropTypes.func.isRequired,
    minimumSearchLength: PropTypes.number,
    onResultsChange: PropTypes.func,
    ownedBooks: PropTypes.arrayOf(PropTypes.string),
    router: PropTypes.shape({
      state: PropTypes.string,
    }).isRequired,
    search: PropTypes.shape({ isLoading: PropTypes.bool, isEmpty: PropTypes.bool }),
    searchBoxFocused: PropTypes.bool,
    searchEndpoint: PropTypes.string,
    searchIsEmpty: PropTypes.func.isRequired,
    searchIsLoading: PropTypes.func.isRequired,
    searchOnLoad: PropTypes.bool,
    settingsResolved: PropTypes.bool,
    showAlacarte: PropTypes.bool,
    userCountryCode: PropTypes.string,
    userDietLimit: PropTypes.arrayOf(PropTypes.string),
    userDietPrefer: PropTypes.arrayOf(PropTypes.string),
    userCulinaryTopics: PropTypes.arrayOf(PropTypes.string),
    userCulinaryRegions: PropTypes.arrayOf(PropTypes.string),
    userIsLoading: PropTypes.bool,
  };

  static defaultProps = {
    allowEmptySearch: false,
    connected: true,
    defaultQuery: query =>
      query.addFilter(
        'available',
        BoolMust([TermQuery('is_licensed', true), TermQuery('is_indexable', true)]),
      ),
    getLocation: () => ({
      pathname: history.location.pathname === '/search/fulltext' ? '/search/fulltext' : '/search',
      search: history.location.search,
    }),
    includeTags: null,
    isBackNavigation: false,
    minimumSearchLength: 1,
    onResultsChange: () => {},
    ownedBooks: [],
    searchBoxFocused: false,
    searchEndpoint: '/es-search',
    searchOnLoad: true,
    settingsResolved: false,
    search: {
      isLoading: false,
    },
    showAlacarte: false,
    userCountryCode: null,
    userDietLimit: [],
    userDietPrefer: [],
    userCulinaryTopics: [],
    userCulinaryRegions: [],
    userIsLoading: false,
  };

  static urlCarriesSearch(params) {
    if (params.q) {
      return true;
    }
    return Object.values(FILTERS).some(filter => {
      const value = params[filter];
      return Array.isArray(value) ? value.length > 0 : !!value;
    });
  }

  constructor(props) {
    super(props);

    this.handler = {
      onChange: null,
      onResultsChange: null,
    };

    this.prevSearchIsEmpty = props.search?.isEmpty;

    this.bound = {
      queryProcessor: this.queryProcessor.bind(this),
      onChange: this.onChange.bind(this),
      onResultsChange: this.onResultsChange.bind(this),
      onSearchSessionChange: this.onSearchSessionChange.bind(this),
    };

    this.searchkit = new SearchkitManagerCustom(props.searchEndpoint, {
      useHistory: !!process.env.BROWSER,
      createHistory: () => history,
      searchOnLoad: props.searchOnLoad,
      getLocation: props.getLocation,
    });

    this.searchkit.setQueryProcessor(this.bound.queryProcessor);
    this.searchkit.addDefaultQuery(this.props.defaultQuery);
    this.searchkit.setSearchSessionListener(this.bound.onSearchSessionChange);

    this.awaitingInitialResults = history.location.pathname === '/search';
    this.appliedDietLimit = props.userDietLimit;
    this.dietAdopted = false;
    this.appliedDietPrefer = props.userDietPrefer;
    this.appliedCulinary = this.culinaryPreference(props);
  }

  componentDidMount() {
    this.handler.onChange = this.searchkit.emitter.addListener(this.bound.onChange);
    this.handler.onResultsChange = this.searchkit.addResultsListener(this.bound.onResultsChange);
    this.syncNutritionToSearch();
  }

  componentDidUpdate(prevProps) {
    const query = qs.parse(history.location.search.replace(/^\?/, ''));
    const sameQuery = Object.keys(query).reduce((previousResult, key) => {
      if (previousResult) {
        return isEqual(query[key], this.searchkit.state[key]);
      }
      return previousResult;
    }, true);

    // When should we trigger a reset of the query?
    const isSearchView = history.location.pathname === '/search';

    // The URL only catches up with the box when a search is committed, so a navigation that finishes
    // while the reader is still typing lands on an older query. That query must not replace their text.
    const isNewSearch = isSearchView && !sameQuery && !this.searchkit.queryBeingTyped;
    const navigationComplete =
      prevProps.router.state === 'loading' && this.props.router.state === 'loaded';

    // Don't reset search results when navigating back to preserve the previous search state
    const shouldReset =
      this.props.connected &&
      navigationComplete &&
      isNewSearch &&
      !this.props.userIsLoading &&
      !this.props.isBackNavigation;

    if (shouldReset) {
      this.dietAdopted = false;
      const resetAccessor = this.searchkit.accessors.getAccessorByType(ResetSearchAccessor);
      if (resetAccessor) {
        resetAccessor.performReset();
        if (history.location.search) {
          this.searchkit.searchFromUrlQuery(history.location.search);
        }
      }
    }

    this.syncNutritionToSearch();
  }

  componentWillUnmount() {
    this.handler.onChange();
    this.handler.onResultsChange();
  }

  /**
   * `onChange` handler. Triggered before and after search.
   */
  onChange() {
    if (this.props.search.isLoading !== this.searchkit.loading) {
      this.props.searchIsLoading(this.searchkit.loading);
    }
  }

  onSearchSessionChange(session) {
    this.logSearch(session.state, session.results);
  }

  onResultsChange() {
    const { accessors } = this.searchkit;
    const { statefulAccessors } = accessors;
    if (this.awaitingInitialResults && this.searchkit.results) {
      this.logSearch(this.searchkit.state, this.searchkit.results);
      this.awaitingInitialResults = false;
    }

    const areFiltersEmpty = Object.values(FILTERS).every(filter =>
      search.isAccessorEmpty(statefulAccessors[filter]),
    );

    if (areFiltersEmpty === false) {
      this.props.featureUsed('feature_search_filters');
    }

    // Avoid flipping the empty state while a search (incl. pagination) is in progress
    // and only dispatch when the value actually changes.
    const pageValue = get(this.searchkit, 'accessors.statefulAccessors.p.state.value', 0) || 0;
    const isPagination = Number(pageValue) > 0;
    const nextIsEmpty = this.isEmpty();

    if (!this.searchkit.loading && !isPagination) {
      if (this.prevSearchIsEmpty !== nextIsEmpty) {
        this.props.searchIsEmpty(nextIsEmpty);
        this.prevSearchIsEmpty = nextIsEmpty;
      }
    }

    const changeHook = this.props.onResultsChange;
    changeHook(this.searchkit.results, this.searchkit);
  }

  async logSearch(state, results) {
    let resultCount = null;
    if (results) {
      resultCount = results.hits.total;
    }
    if (state.q) {
      try {
        await this.props.logAccess({
          accessType: ACCESS_TYPE_NORMAL,
          contentType: CONTENT_TYPES.SEARCH,
          id: JSON.stringify(state),
          resultCount,
        });
        sendEvent({
          event: 'Search.Query',
          query: state,
          resultCount,
        });
      } catch (err) {
        console.error('Cannot log search', err);
      }
    }
  }

  isEmpty() {
    const { minimumSearchLength } = this.props;
    const { accessors } = this.searchkit;
    return search.isEmptyState(accessors, FILTERS, minimumSearchLength);
  }

  hasNoPreviousSearch() {
    const { minimumSearchLength } = this.props;
    const { accessors } = this.searchkit;
    const dietAccessor = accessors.statefulAccessors[FILTERS.DIET_TYPE];
    const currentDiet =
      dietAccessor && Array.isArray(dietAccessor.state.value) ? dietAccessor.state.value : [];
    const surfacedDiet = getPrimaryNutritionDietSearchValues(this.appliedDietLimit);
    const filters = isEqual(currentDiet, surfacedDiet) ? omit(FILTERS, 'DIET_TYPE') : FILTERS;
    return search.isEmptyState(accessors, filters, minimumSearchLength);
  }

  hasFreshSearchIntent() {
    const { searchBoxFocused, isBackNavigation } = this.props;
    if (isBackNavigation) {
      return false;
    }
    const locationState = history.location.state || {};
    if (locationState.universalLink) {
      return false;
    }
    const params = qs.parse(history.location.search.replace(/^\?/, ''));
    if (params[SAMPLE_SEARCH_PARAM]) {
      return false;
    }
    if (locationState.searchIntent) {
      return SearchProvider.urlCarriesSearch(params) || this.hasNoPreviousSearch();
    }
    // A URL that already describes a search belongs to whoever wrote it, so only
    // a blank search screen is ours to seed.
    const onBlankSearchRoute =
      history.location.pathname.startsWith('/search') && !SearchProvider.urlCarriesSearch(params);
    if (onBlankSearchRoute) {
      return this.hasNoPreviousSearch();
    }
    return searchBoxFocused && this.hasNoPreviousSearch();
  }

  // Culinary interests only re-rank, like the diet Prefer setting.
  culinaryPreference({ userCulinaryTopics, userCulinaryRegions }) {
    return { topics: userCulinaryTopics, regions: userCulinaryRegions };
  }

  syncNutritionToSearch() {
    const { userDietPrefer, userIsLoading, connected, settingsResolved } = this.props;
    if (!process.env.BROWSER || !connected || userIsLoading || !settingsResolved) {
      return;
    }
    const triggeredSearch = this.syncDietLimitToSearch();
    // Prefer only re-ranks, so it applies to every search on every entry path.
    const culinary = this.culinaryPreference(this.props);
    if (
      isEqual(this.appliedDietPrefer, userDietPrefer) &&
      isEqual(this.appliedCulinary, culinary)
    ) {
      return;
    }
    if (triggeredSearch) {
      this.appliedDietPrefer = userDietPrefer;
      this.appliedCulinary = culinary;
    } else if (this.searchkit.results) {
      // Leave it unrecorded until results exist, or the displayed ranking keeps
      // the old preference with nothing left to trigger a re-run.
      this.appliedDietPrefer = userDietPrefer;
      this.appliedCulinary = culinary;
      this.searchkit.reloadWithoutHistory();
    }
  }

  syncDietLimitToSearch() {
    const { userDietLimit } = this.props;
    const dietAccessor = this.searchkit.accessors.statefulAccessors[FILTERS.DIET_TYPE];
    if (!dietAccessor) {
      return false;
    }
    const limitValues = getPrimaryNutritionDietSearchValues(userDietLimit);
    const currentDiet = Array.isArray(dietAccessor.state.value) ? dietAccessor.state.value : [];

    // Runs even when the gate is shut, so an edit of the setting is never missed. The first
    // evaluation adopts whatever is saved rather than reading it as an edit, because the settings
    // arrive after the constructor has already mirrored an empty default.
    const settingChanged = this.dietAdopted && !isEqual(userDietLimit, this.appliedDietLimit);
    this.appliedDietLimit = userDietLimit;
    this.dietAdopted = true;

    // Only a search this app started, or an edit of the setting, may write diet_type.
    if (!settingChanged && !this.hasFreshSearchIntent()) {
      return false;
    }
    if (isEqual(currentDiet, limitValues)) {
      return false;
    }
    // A diet already on screen that is not the one we would seed can only be the reader's, and
    // survives them submitting their next query. An empty one is indistinguishable from a search
    // that was never seeded, so that case reads the marker the filter bar leaves on the entry.
    if (
      !settingChanged &&
      (currentDiet.length > 0 || dietWasTouched(this.searchkit.touchedFilterFields()))
    ) {
      return false;
    }
    const onSearchRoute = history.location.pathname.startsWith('/search');
    const locationState = history.location.state || {};
    if (onSearchRoute && locationState.searchIntent) {
      // Spend the intent: the URL we write must behave like the link it becomes.
      this.searchkit.pendingLocationState = omit(locationState, 'searchIntent');
    }
    dietAccessor.state = dietAccessor.state.setValue(limitValues);
    this.searchkit.performSearch(onSearchRoute, true, onSearchRoute);
    return true;
  }

  queryProcessor(queryObject) {
    const {
      alacarteEnabled,
      allowEmptySearch,
      minimumSearchLength,
      includeTags,
      ownedBooks,
      showAlacarte,
      userCountryCode,
      userDietPrefer,
      userCulinaryTopics,
      userCulinaryRegions,
    } = this.props;
    const { accessors } = this.searchkit;

    // Deep copy the query. Searchkit does strange things if you mutate the queryObject
    const newQueryObject = JSON.parse(JSON.stringify(queryObject));

    // Filter out hidden content.
    // The user might have some tags that they want to query on, and
    // content matching those tags should override the hidden filter.
    const filters = [];
    if (includeTags && includeTags.length) {
      // Remove hidden content, but include some tags which may include hidden content.
      filters.push(
        BoolShould([
          TermQuery('is_hidden', false),
          ...includeTags.map(tag => TermQuery('book_tags', tag)),
        ]),
      );
    } else {
      // Just remove hidden content without querying tags
      filters.push(TermQuery('is_hidden', false));
    }
    // By default filter out alacarte content unless the user owns it
    const alacarteFilters = [
      // Always show content that is not alacarte
      TermQuery('is_alacarte', false),
      // always show content that you own
      TermsQuery('book_isbn', ownedBooks || []),
      // always show authors and collections regardless of the alacarte setting
      TermsQuery('label.raw', ['book', 'person', 'collection']),
    ];
    if (showAlacarte) {
      // If the user settings allow unpurchased alacarte then we will include it, but only items that can be purchased in that country
      const showAlacarteForRegion = [TermQuery('is_alacarte', true)];
      if (userCountryCode) {
        showAlacarteForRegion.push(TermQuery('allow_purchase_from', userCountryCode));
      }
      alacarteFilters.push(BoolMust(showAlacarteForRegion));
    }
    if (alacarteEnabled) {
      filters.push(BoolShould(alacarteFilters));
    }
    // We need to add that filter to the main post_filter and the filters for all aggregations
    const filterLists = [newQueryObject.post_filter?.bool?.must];
    filterLists.push(...Object.values(newQueryObject.aggs).map(agg => agg.filter?.bool?.must));
    filterLists.forEach(filterList => {
      if (Array.isArray(filterList)) {
        filterList.push(...filters);
      }
    });

    // Recipes that are Vegetarian or Vegan qualify to be displayed for the Pescatarian filter.
    // However, if I set the diet type filter to be Pescatarian on its own then I'm probably looking for fish.
    // We can put a negative boost on to documents in the results that match 'diet-type=Vegetarian' (or Vegan) in these cases.
    // See https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-boosting-query.html
    const negativeBoosts = [];

    const dietTypeAccessor = accessors.statefulAccessors.diet_type;
    if (dietTypeAccessor) {
      const queryText = accessors.queryAccessor.state.value;
      const dietTypeFilter = dietTypeAccessor.state.value;
      const queryTextIsPescatarian =
        queryText &&
        (queryText.toLowerCase().includes('pescatar') ||
          queryText.toLowerCase().includes('pescetar'));

      const dietTypeFilterIsPescatarian =
        Array.isArray(dietTypeFilter) && dietTypeFilter.includes('Pescatarian');

      if (queryTextIsPescatarian || dietTypeFilterIsPescatarian) {
        ['Vegetarian', 'Vegan'].forEach(dietTypeToSubdue => {
          if (!(dietTypeFilter && dietTypeFilter.includes(dietTypeToSubdue))) {
            negativeBoosts.push({ term: { [dietTypeAccessor.options.field]: dietTypeToSubdue } });
          }
        });
      }
    }
    if (!allowEmptySearch && search.isEmptyState(accessors, FILTERS, minimumSearchLength)) {
      // Stops search action
      return {
        size: 0,
        query: {
          match_none: {},
        },
      };
    }

    // Name the shared filters the reader chose, so the proxy narrows every content type by them
    // instead of only the ones that index them. Values come from the accessors rather than the
    // address bar: the URL still describes the previous search while this query is being built.
    const ownedFilters = ownedFilterFields({
      filterValues: SHARED_FILTERS.reduce((values, field) => {
        const value = accessors.statefulAccessors[field]?.state?.value;
        return { ...values, [field]: Array.isArray(value) ? value : [] };
      }, {}),
      touchedFields: this.searchkit.touchedFilterFields(),
    });
    if (ownedFilters.length > 0) {
      newQueryObject[OWNED_FILTERS_QUERY_KEY] = ownedFilters;
    }

    // The saved Prefer setting re-ranks matching recipes without excluding the
    // rest. The search proxy converts this clause into a multiplicative weight.
    const preferSearchValues =
      userDietPrefer && userDietPrefer.length > 0
        ? getNutritionDietSearchValues(userDietPrefer)
        : [];

    const hasNegativeBoosts =
      negativeBoosts.length > 0 && newQueryObject.query && !newQueryObject.query.boosting;
    const culinarySearchValues = getCulinarySearchValues({ topics: userCulinaryTopics });
    const { continents, countries } = getCulinaryRegionTerms({ regions: userCulinaryRegions });
    const preferClauses = [];
    if (preferSearchValues.length > 0) {
      preferClauses.push({
        terms: {
          'diet_type.raw': preferSearchValues,
          boost: NUTRITION_DIET_PREFER_BOOST,
        },
      });
    }
    if (culinarySearchValues.length > 0) {
      preferClauses.push({
        multi_match: {
          _name: CULINARY_PREFER_CLAUSE_NAME,
          query: culinarySearchValues.join(' '),
          fields: CULINARY_PREFER_FIELDS,
          operator: 'or',
          boost: CULINARY_PREFER_BOOST,
        },
      });
    }
    if (continents.length > 0 || countries.length > 0) {
      preferClauses.push({
        bool: {
          _name: CULINARY_REGION_PREFER_CLAUSE_NAME,
          should: [
            ...(continents.length > 0 ? [{ terms: { 'continent.raw': continents } }] : []),
            ...(countries.length > 0 ? [{ terms: { 'country.raw': countries } }] : []),
          ],
          minimum_should_match: 1,
          boost: CULINARY_PREFER_BOOST,
        },
      });
    }
    const hasPreferBoost = preferClauses.length > 0 && !!newQueryObject.query;

    if (hasPreferBoost || hasNegativeBoosts) {
      let wrappedQuery = newQueryObject.query;
      if (hasPreferBoost) {
        wrappedQuery = {
          bool: {
            must: [wrappedQuery],
            should: preferClauses,
          },
        };
      }
      if (hasNegativeBoosts) {
        // Wrap with a 'boosting' query that demotes matches against `negative`.
        wrappedQuery = {
          boosting: {
            positive: wrappedQuery,
            negative: { bool: { should: negativeBoosts } },
            negative_boost: 0.5,
          },
        };
      }
      return {
        ...newQueryObject,
        query: wrappedQuery,
      };
    }

    return newQueryObject;
  }

  render() {
    const { children } = this.props;
    return (
      <SearchkitProvider searchkit={this.searchkit}>
        <React.Fragment>
          <div style={{ display: 'none' }}>
            <MenuFilter id="label" field="label.raw" title="Label" />
            <ResetFilters />
            {/* There must be a sorting selector in the page to parse the query string sort parameter.
          This is hidden - another instance is rendered above the Recipe search results for the user to intract with.
          This is a workaround for the fact that the Recipes results are not yet rendered when the page loads. */}
            <SortingSelector options={SORT_OPTIONS} />
          </div>
          <SearchkitContext.Provider value={this.searchkit}>{children}</SearchkitContext.Provider>
        </React.Fragment>
      </SearchkitProvider>
    );
  }
}

const shouldShowAlaCarteContent = user => {
  if (typeof user.settings?.show_alacarte_content === 'boolean') {
    return user.settings?.show_alacarte_content;
  }

  if (typeof user.geolocation.corporate === 'boolean') {
    return !user.geolocation.corporate;
  }

  return true;
};

// A factory, so the memo is per-instance. Mapping oven_brands inline returns a
// new array every call, which re-renders the provider on every redux action.
const makeMapStateToProps = () => {
  let lastOvenBrands;
  let lastIncludeTags;
  const selectIncludeTags = ovenBrands => {
    if (ovenBrands !== lastOvenBrands) {
      lastOvenBrands = ovenBrands;
      lastIncludeTags = ovenBrands ? ovenBrands.map(brand => brand.toLowerCase()) : undefined;
    }
    return lastIncludeTags;
  };

  return state => ({
    connected: state.device.connected,
    includeTags: selectIncludeTags(state.user.oven_brands),
    isBackNavigation: state.router?.isBackNavigation || false,
    ownedBooks: state.user.owned_books || [],
    router: state.router,
    search: state.search,
    searchEndpoint: `${state.config.apiUrl}/es-search`,
    showAlacarte: shouldShowAlaCarteContent(state.user),
    userCountryCode: state.user.geolocation.countryCode,
    userDietLimit: Array.isArray(state.user.settings?.nutrition_diet_limit)
      ? state.user.settings.nutrition_diet_limit
      : [],
    userDietPrefer: Array.isArray(state.user.settings?.nutrition_diet_prefer)
      ? state.user.settings.nutrition_diet_prefer
      : [],
    userCulinaryTopics: Array.isArray(state.user.settings?.culinary_topics)
      ? state.user.settings.culinary_topics
      : [],
    userCulinaryRegions: Array.isArray(state.user.settings?.culinary_regions)
      ? state.user.settings.culinary_regions
      : [],
    userIsLoading: state.user.loading,
    settingsResolved: state.user.settingsResolved || false,
  });
};

const mapDispatchToProps = {
  featureUsed,
  logAccess,
  searchIsEmpty,
  searchIsLoading,
};

export default connect(
  makeMapStateToProps,
  mapDispatchToProps,
)(withFeatureToggle(SearchProvider, 'alacarte'));
